import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { 
  Award, 
  TrendingUp, 
  Gift, 
  Star, 
  ChevronRight,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  Trophy,
  Crown,
  Sparkles,
  Users,
  DollarSign,
  Share2,
  Instagram,
  Facebook,
  Twitter,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
  Clock
} from "lucide-react";
import { API } from "../../apiConfig";


const tierColors = {
  Bronze: "from-amber-600 to-amber-800",
  Silver: "from-slate-400 to-slate-600",
  Gold: "from-yellow-400 to-yellow-600",
  Platinum: "from-purple-400 to-purple-600"
};

const tierIcons = {
  Bronze: Award,
  Silver: Star,
  Gold: Crown,
  Platinum: Sparkles
};

const PointsDashboard = ({ token }) => {
  const [pointsData, setPointsData] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemDialog, setRedeemDialog] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  
  // Social post submission state
  const [socialPostDialog, setSocialPostDialog] = useState(false);
  const [socialPosts, setSocialPosts] = useState([]);
  const [postPlatform, setPostPlatform] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [submittingPost, setSubmittingPost] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, historyRes, leaderboardRes, configRes, socialRes] = await Promise.all([
        axios.get(`${API}/points/balance`, { headers }),
        axios.get(`${API}/points/history?limit=10`, { headers }),
        axios.get(`${API}/points/leaderboard?limit=5`),
        axios.get(`${API}/points/config`),
        axios.get(`${API}/points/social-posts`, { headers }).catch(() => ({ data: { posts: [] } }))
      ]);
      
      setPointsData(balanceRes.data);
      setHistory(historyRes.data || []);
      setLeaderboard(leaderboardRes.data || []);
      setConfig(configRes.data);
      setSocialPosts(socialRes.data?.posts || []);
    } catch (error) {
      console.error("Error fetching points data:", error);
      if (error.response?.status === 403) {
        toast.error("Ambassador access required");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    const points = parseInt(redeemAmount);
    if (!points || points < (config?.redemption?.minimum_redemption || 500)) {
      toast.error(`Minimum redemption is ${config?.redemption?.minimum_redemption || 500} points`);
      return;
    }
    if (points > pointsData?.current_balance) {
      toast.error("Insufficient points balance");
      return;
    }

    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/points/redeem`, { points }, { headers });
      toast.success(`Redeemed ${points} points for $${res.data.credit.amount.toFixed(2)} store credit!`);
      setRedeemDialog(false);
      setRedeemAmount("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Redemption failed");
    } finally {
      setRedeeming(false);
    }
  };

  const handleSubmitSocialPost = async () => {
    if (!postPlatform || !postUrl) {
      toast.error("Please select a platform and enter the post URL");
      return;
    }

    setSubmittingPost(true);
    try {
      await axios.post(`${API}/points/social-post`, {
        platform: postPlatform,
        post_url: postUrl
      }, { headers });
      
      toast.success("Social post submitted for review!");
      setSocialPostDialog(false);
      setPostPlatform("");
      setPostUrl("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit post");
    } finally {
      setSubmittingPost(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const TierIcon = pointsData?.tier ? tierIcons[pointsData.tier.name] || Award : Award;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-muted rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!pointsData) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Points Program</h3>
          <p className="text-muted-foreground">
            Ambassador points data not available
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="points-dashboard">
      {/* Hero Card - Current Balance & Tier */}
      <Card className={`bg-gradient-to-br ${tierColors[pointsData.tier?.name] || tierColors.Bronze} text-white overflow-hidden relative`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <CardContent className="p-8 relative">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TierIcon className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">{pointsData.tier?.name} Member</span>
              </div>
              <h2 className="text-5xl font-bold mb-2">
                {pointsData.current_balance.toLocaleString()}
              </h2>
              <p className="text-white/80">Available Points</p>
              <p className="text-lg font-medium mt-2">
                = ${pointsData.redemption_value.toFixed(2)} Store Credit
              </p>
            </div>
            
            <Button 
              onClick={() => setRedeemDialog(true)}
              disabled={!pointsData.can_redeem}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              variant="outline"
            >
              <Gift className="w-4 h-4 mr-2" />
              Redeem Points
            </Button>
          </div>

          {/* Progress to Next Tier */}
          {pointsData.next_tier_progress && (
            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="flex justify-between text-sm mb-2">
                <span>{pointsData.tier?.name}</span>
                <span>{pointsData.next_tier_progress.next_tier}</span>
              </div>
              <Progress 
                value={pointsData.next_tier_progress.progress_percent} 
                className="h-2 bg-white/20"
              />
              <p className="text-xs mt-2 opacity-80">
                {pointsData.next_tier_progress.points_needed.toLocaleString()} more points to {pointsData.next_tier_progress.next_tier}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-green-100">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pointsData.lifetime_earned.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Lifetime Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-100">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pointsData.lifetime_redeemed.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Points Redeemed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-amber-100">
                <Coins className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pointsData.tier?.bonus_multiplier}x</p>
                <p className="text-sm text-muted-foreground">Earning Multiplier</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How to Earn Points */}
      {config && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              How to Earn Points
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="p-3 rounded-full bg-green-100">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{config.earning.per_dollar_sale} points</p>
                  <p className="text-sm text-muted-foreground">Per $1 of referred sales</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="p-3 rounded-full bg-blue-100">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{config.earning.per_referral} points</p>
                  <p className="text-sm text-muted-foreground">Per new customer referral</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="p-3 rounded-full bg-purple-100">
                  <Share2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">{config.earning.per_social_post} points</p>
                  <p className="text-sm text-muted-foreground">Per social media post</p>
                </div>
              </div>
            </div>
            
            {/* Submit Social Post Button */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Share on Social Media</h4>
                  <p className="text-sm text-muted-foreground">Submit your posts to earn {config.earning.per_social_post} points each</p>
                </div>
                <Button onClick={() => setSocialPostDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Share2 className="w-4 h-4 mr-2" /> Submit Post
                </Button>
              </div>
              
              {/* Recent submissions */}
              {socialPosts.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Recent Submissions</p>
                  {socialPosts.slice(0, 3).map(post => (
                    <div key={post.submission_id} className="flex items-center justify-between text-sm bg-white p-2 rounded">
                      <span className="capitalize">{post.platform}</span>
                      {getStatusBadge(post.status)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No points activity yet. Start earning by referring sales!
              </p>
            ) : (
              <div className="space-y-3">
                {history.map((tx) => (
                  <div 
                    key={tx.transaction_id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        tx.type === 'earn' ? 'bg-green-100' : 'bg-purple-100'
                      }`}>
                        {tx.type === 'earn' ? (
                          <ArrowUpRight className="w-4 h-4 text-green-600" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tx.reason}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${
                        tx.type === 'earn' ? 'text-green-600' : 'text-purple-600'
                      }`}>
                        {tx.type === 'earn' ? '+' : ''}{tx.total_points.toLocaleString()}
                      </p>
                      {tx.bonus_points > 0 && (
                        <p className="text-xs text-amber-600">+{tx.bonus_points} bonus</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Top Ambassadors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Be the first on the leaderboard!
              </p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((leader, idx) => (
                  <div 
                    key={leader.user_id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      idx === 0 ? 'bg-amber-50 border border-amber-200' :
                      idx === 1 ? 'bg-slate-50 border border-slate-200' :
                      idx === 2 ? 'bg-orange-50 border border-orange-200' :
                      'bg-muted/30'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-amber-500 text-white' :
                      idx === 1 ? 'bg-slate-400 text-white' :
                      idx === 2 ? 'bg-orange-400 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {leader.rank}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{leader.name || 'Ambassador'}</p>
                      <Badge variant="outline" className="text-xs">{leader.tier}</Badge>
                    </div>
                    <p className="font-bold">{leader.lifetime_earned.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tier Benefits */}
      {config && (
        <Card>
          <CardHeader>
            <CardTitle>Tier Benefits</CardTitle>
            <CardDescription>Earn more points to unlock higher tiers and exclusive perks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Object.entries(config.tiers).map(([key, tier]) => (
                <div 
                  key={key}
                  className={`p-4 rounded-xl border-2 ${
                    pointsData.tier?.name === tier.name 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {key === 'platinum' && <Crown className="w-5 h-5 text-purple-500" />}
                    {key === 'gold' && <Crown className="w-5 h-5 text-yellow-500" />}
                    {key === 'silver' && <Star className="w-5 h-5 text-slate-400" />}
                    {key === 'bronze' && <Award className="w-5 h-5 text-amber-600" />}
                    <span className="font-bold">{tier.name}</span>
                    {pointsData.tier?.name === tier.name && (
                      <Badge className="ml-auto">Current</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {tier.min_points.toLocaleString()}+ points
                  </p>
                  <p className="text-sm font-medium text-green-600 mb-3">
                    {tier.bonus_multiplier}x earning multiplier
                  </p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    {tier.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Redeem Dialog */}
      <Dialog open={redeemDialog} onOpenChange={setRedeemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redeem Points for Store Credit</DialogTitle>
            <DialogDescription>
              Convert your points to store credit. {config?.redemption?.points_per_dollar || 100} points = $1.00
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Available: <span className="font-bold text-foreground">{pointsData.current_balance.toLocaleString()}</span> points
              </p>
              <p className="text-sm text-muted-foreground">
                Minimum: <span className="font-medium">{config?.redemption?.minimum_redemption || 500}</span> points
              </p>
            </div>
            
            <div>
              <Input
                type="number"
                placeholder="Enter points to redeem"
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                min={config?.redemption?.minimum_redemption || 500}
                max={pointsData.current_balance}
              />
              {redeemAmount && (
                <p className="text-sm mt-2">
                  = <span className="font-bold text-green-600">
                    ${(parseInt(redeemAmount) / (config?.redemption?.points_per_dollar || 100)).toFixed(2)}
                  </span> store credit
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setRedeemAmount(String(config?.redemption?.minimum_redemption || 500))}
              >
                Min ({config?.redemption?.minimum_redemption || 500})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setRedeemAmount(String(Math.floor(pointsData.current_balance / 2)))}
              >
                Half
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setRedeemAmount(String(pointsData.current_balance))}
              >
                All
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRedeem} disabled={redeeming}>
              <Gift className="w-4 h-4 mr-2" />
              {redeeming ? "Redeeming..." : "Redeem Points"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Post Submission Dialog */}
      <Dialog open={socialPostDialog} onOpenChange={setSocialPostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-purple-600" />
              Submit Social Media Post
            </DialogTitle>
            <DialogDescription>
              Share Art By Arawol on social media and earn {config?.earning?.per_social_post || 25} points per approved post!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={postPlatform} onValueChange={setPostPlatform}>
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="twitter">Twitter / X</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="pinterest">Pinterest</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Post URL</Label>
              <Input
                placeholder="https://instagram.com/p/..."
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Paste the direct link to your post
              </p>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="font-medium text-amber-700 mb-1">Guidelines:</p>
              <ul className="text-amber-600 space-y-1 text-xs">
                <li>• Post must feature or mention Art By Arawol</li>
                <li>• Post must be publicly visible</li>
                <li>• Points awarded after admin review</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSocialPostDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitSocialPost} 
              disabled={submittingPost || !postPlatform || !postUrl}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submittingPost ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Share2 className="w-4 h-4 mr-2" /> Submit Post</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PointsDashboard;
